import { useEffect, useState } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const ROLE_LABELS = { admin: 'Admin', editor: 'Editor', manager: 'Manager' };
const ROLE_COLORS = { admin: 'badge-soft-danger', editor: 'badge-soft-primary', manager: 'badge-soft-success' };
const ROLE_DESCRIPTIONS = {
  admin: 'Full access — can create, edit, and delete everything',
  editor: 'Can edit all content (events, gallery, news, etc.) but cannot delete or manage members',
  manager: 'Can view members (read-only), no content editing',
};

const EMPTY_FORM = { name: '', email: '', password: '', role: 'editor' };

export default function UserManager() {
  const { admin: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/auth/users').then(r => setUsers(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM); setError(''); setShowForm(true); };
  const openEdit = (u) => {
    setEditTarget(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role || 'editor' });
    setError(''); setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (editTarget && !payload.password) delete payload.password;
      if (editTarget) {
        await api.put(`/auth/users/${editTarget.id}`, payload);
      } else {
        await api.post('/auth/users', payload);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/auth/users/${id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">User Management</h4>
          <p className="text-muted small mb-0">Manage admin panel users and their access levels</p>
        </div>
        <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}>
          <i className="fas fa-plus me-2"></i>Add User
        </button>
      </div>

      <div className="row g-3 mb-4">
        {Object.entries(ROLE_DESCRIPTIONS).map(([role, desc]) => (
          <div key={role} className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-3">
                <span className={`badge ${ROLE_COLORS[role]} mb-2`}>{ROLE_LABELS[role]}</span>
                <p className="text-muted small mb-0">{desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3">{editTarget ? 'Edit User' : 'Add User'}</h5>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <form onSubmit={save}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Email *</label>
                  <input type="email" className="form-control" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">
                    Password {editTarget && <span className="fw-normal text-muted">(leave blank to keep current)</span>}
                  </label>
                  <input type="password" className="form-control" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} {...(!editTarget && { required: true })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Role *</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="admin">Admin — full access</option>
                    <option value="editor">Editor — edit content, no delete</option>
                    <option value="manager">Manager — view members only</option>
                  </select>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4">
                <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
                  {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Add User'}
                </button>
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm">
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="small text-muted fw-semibold">Name</th>
                  <th className="small text-muted fw-semibold">Email</th>
                  <th className="small text-muted fw-semibold">Role</th>
                  <th className="small text-muted fw-semibold">Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan="5" className="text-center text-muted py-4">No users yet.</td></tr>
                )}
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="fw-semibold">
                      {u.name}
                      {u.id === me?.id && <span className="badge badge-soft-secondary ms-2 small">You</span>}
                    </td>
                    <td className="small">{u.email}</td>
                    <td><span className={`badge ${ROLE_COLORS[u.role] || 'badge-soft-secondary'}`}>{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="small text-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary rounded-pill me-2" onClick={() => openEdit(u)}>
                        <i className="fas fa-edit me-1"></i>Edit
                      </button>
                      {u.id !== me?.id && (
                        <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => remove(u.id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
